// Feature flags, switched by ZMAP admins and published by the API as
// GET /features -> {features: {key: bool}, version}. The server also refuses
// the routes of a switched-off feature (403 feature_disabled), so this only
// decides what the UI shows.
//
// Fail open: before the first load, or if the fetch fails, a flag counts as
// ON - a flaky network must never hide the product. The last good list is
// cached in localStorage so a reload doesn't flash switched-off UI back on.
import { useSyncExternalStore } from 'react';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';
const CACHE_KEY = 'zmist.features.v1';
const REFRESH_MS = 60_000;

export type FeatureFlags = Record<string, boolean>;

function readCache(): FeatureFlags {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as FeatureFlags) : {};
  } catch {
    return {};
  }
}

let flags: FeatureFlags = readCache();
let version = '';
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

// Accepts the {features, version} body (optionally wrapped in {data}).
export function parseFeatures(body: unknown): { flags: FeatureFlags; version: string } | null {
  const root = body as { data?: unknown } | null;
  const data = (root && typeof root === 'object' && 'data' in root && root.data ? root.data : body) as {
    features?: unknown;
    version?: unknown;
  } | null;
  if (!data || typeof data !== 'object' || !data.features || typeof data.features !== 'object') return null;
  const out: FeatureFlags = {};
  for (const [k, v] of Object.entries(data.features as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return { flags: out, version: typeof data.version === 'string' ? data.version : '' };
}

export function setFeatures(next: FeatureFlags, nextVersion = ''): void {
  const same = nextVersion !== '' && nextVersion === version;
  flags = next;
  version = nextVersion;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch {
    // storage unavailable - in-memory is enough
  }
  if (!same) emit();
}

export async function refreshFeatures(): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/features`, { credentials: 'include' });
    if (!response.ok) return;
    const parsed = parseFeatures(await response.json());
    if (parsed) setFeatures(parsed.flags, parsed.version);
  } catch {
    // keep the last known flags
  }
}

let started = false;
// Idempotent; call once from the app root.
export function startFeatureSync(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshFeatures();
  window.setInterval(() => void refreshFeatures(), REFRESH_MS);
  window.addEventListener('focus', () => void refreshFeatures());
}

export function isFeatureOn(key: string): boolean {
  return flags[key] !== false;
}

// Non-hook twin of useFeatureStrict, for plain modules (dataApi): ON only
// once the server has explicitly reported the flag true.
export function isFeatureStrict(key: string): boolean {
  return flags[key] === true;
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Re-renders the caller when the flag flips.
export function useFeature(key: string): boolean {
  return useSyncExternalStore(subscribe, () => flags[key] !== false, () => true);
}

// Test hook.
export function resetFeaturesForTest(next: FeatureFlags = {}): void {
  flags = next;
  version = '';
  emit();
}

// Strict variant for flags that must NOT fail open (e.g. a feature whose
// backend may not be deployed yet): ON only once the server has explicitly
// reported it true.
export function useFeatureStrict(key: string): boolean {
  return useSyncExternalStore(subscribe, () => flags[key] === true, () => false);
}
